import { Component, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { APIAddressbookSearchProperties } from '../../../core/services';
import { ConfigService } from '../../../core/services/config/config.service';
import { ContactSearchLabel, ContactSearchOptions, ContactSearchValue } from '../../../contacts/models/contacts.model';
import { ContactSearchOptionsList } from '../../../contacts/constants/contacts.constants';
import { CountryData, StatesData } from 'ng-voxloud';

@Component({
  selector: 'contact-search',
  templateUrl: './contact-search.component.html',
  styleUrls: ['./contact-search.component.scss'],
  providers: [
    StatesData,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ContactSearchComponent),
      multi: true
    }
  ]
})
export class ContactSearchComponent implements ControlValueAccessor {
  public searchOptions: ContactSearchOptions[] = ContactSearchOptionsList;

  selectedSearchType: ContactSearchLabel;
  private _searchValue: ContactSearchValue;
  private initialSearchType: ContactSearchLabel;
  public defaultCountry: string;
  private currentPhoneCode: string = '';
  disabled = false;

  private onChange: (value: ContactSearchValue) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private configService: ConfigService, private statesData: StatesData) {
    const defaultSearchType = this.getInitialSearchType();
    this.initialSearchType = defaultSearchType;
    this.selectedSearchType = defaultSearchType;
    this.defaultCountry = this.getDefaultCountry();
    this.currentPhoneCode = this.statesData.allCountries.find(s => s.iso2 === this.defaultCountry)?.phoneCode || '';
    this._searchValue = {
      input: '',
      searchProperties: this.mapToAPISearchProperties(defaultSearchType),
      countryPhoneCode: this.currentPhoneCode
    };
  }

  private getInitialSearchType(): ContactSearchLabel {
    const configSearchProperty = this.configService.config?.settings?.contactSearch?.searchProperty;

    if (configSearchProperty === 'phonenumber') {
      return ContactSearchLabel.PHONE_NUMBER;
    } else if (configSearchProperty === 'fullname') {
      return ContactSearchLabel.FULL_NAME;
    }

    return this.searchOptions[0].label;
  }

  private getDefaultCountry(): string {
    const configCountry = this.configService.config?.settings?.contactSearch?.defaultCountry;
    return configCountry || 'it';
  }

  private mapToAPISearchProperties(label: ContactSearchLabel): APIAddressbookSearchProperties[] {
    switch (label) {
      case ContactSearchLabel.FULL_NAME:
        return [
          APIAddressbookSearchProperties.FIRST_NAME,
          APIAddressbookSearchProperties.LAST_NAME
        ];
      case ContactSearchLabel.PHONE_NUMBER:
        return [APIAddressbookSearchProperties.PHONE_NUMBER];
      default:
        return [
          APIAddressbookSearchProperties.FIRST_NAME,
          APIAddressbookSearchProperties.LAST_NAME
        ];
    }
  }

  get searchValue(): string {
    return this._searchValue.input;
  }

  set searchValue(value: string) {
    this._searchValue = {
      input: value,
      searchProperties: this._searchValue.searchProperties,
      countryPhoneCode: this.currentPhoneCode
    };
    this.onChange(this._searchValue);
    this.onTouched();
  }

  onCountryChanged(country: CountryData): void {
    this.currentPhoneCode = country.phoneCode;
    this._searchValue = { ...this._searchValue, countryPhoneCode: country.phoneCode };
    this.onChange(this._searchValue);
  }

  selectSearchType(label: ContactSearchLabel): void {
    if (this.selectedSearchType === label) {
      return;
    }

    this.selectedSearchType = label;
    this._searchValue = {
      input: '',
      searchProperties: this.mapToAPISearchProperties(label),
      countryPhoneCode: this.currentPhoneCode
    };
    this.onChange(this._searchValue);
    this.onTouched();
  }

  clearSearch(): void {
    this.searchValue = '';
  }

  get isNameSearch(): boolean {
    return this.selectedSearchType === ContactSearchLabel.FULL_NAME;
  }

  get isPhoneSearch(): boolean {
    return this.selectedSearchType === ContactSearchLabel.PHONE_NUMBER;
  }

  get selectedIcon(): string {
    const option = this.searchOptions.find(opt => opt.label === this.selectedSearchType);
    return option?.icon || 'icon-small-user';
  }

  writeValue(value: ContactSearchValue): void {
    if (value) {
      this._searchValue = value;
      if (value.countryPhoneCode) {
        this.currentPhoneCode = value.countryPhoneCode;
      }
      if (value.searchProperties?.includes(APIAddressbookSearchProperties.PHONE_NUMBER)) {
        this.selectedSearchType = ContactSearchLabel.PHONE_NUMBER;
      } else {
        this.selectedSearchType = ContactSearchLabel.FULL_NAME;
      }
    } else {
      this._searchValue = {
        input: '',
        searchProperties: this.mapToAPISearchProperties(this.initialSearchType),
        countryPhoneCode: this.currentPhoneCode
      };
      this.selectedSearchType = this.initialSearchType;
    }
  }

  registerOnChange(fn: (value: ContactSearchValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}